import { useState, Fragment, useEffect } from 'react'
import { Box, Button, IconButton, Menu as MuiMenu, MenuItem as MuiMenuItem, Typography, Badge } from '@mui/material'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
// ** Icons Imports
import notificationIcon from '../../../../assets/notification.svg'
// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'
import CustomChip from 'src/@core/components/mui/chip'
import CustomAvatar from 'src/@core/components/mui/avatar'
import { axiosInstance } from 'src/network/adapter'
import { ApiEndPoints } from 'src/network/endpoints'
import { toastError } from 'src/utils/utils'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useAuth } from 'src/hooks/useAuth'
import { useTranslation } from 'react-i18next'

const Menu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 380,
    overflow: 'hidden',
    //backgroundColor: '#fff',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0
  }
}))

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`
}))

const styles = {
  maxHeight: 349,
  '& .MuiMenuItem-root:last-of-type': {
    border: 0
  }
}

const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  ...styles
})

const Avatar = styled(CustomAvatar)({
  width: '2.375rem',
  height: '2.375rem',
  fontSize: '1.125rem'
})

const MenuItemTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  //flex: '1 1 100%',
  overflow: 'hidden',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
  //textOverflow: 'ellipsis',
  marginBottom: theme.spacing(0.75),
  textTransform: 'capitalize'
}))

const MenuItemSubtitle = styled(Typography)({
  // flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textWrap: 'wrap',
  maxWidth: 220
  // textOverflow: 'ellipsis'
})

const getRedirectUrl = notification => {
  const { metaData } = notification
  const type = metaData?.type
  const id = metaData?.id

  switch (type) {
    case 'customerSupportChat':
      return `/chat?support=${id}`
    case 'quotationChat':
      return `/chat?quotation=${id}`
    case 'transaction':
      return `/payments`
    case 'privateQms':
      return `/my-qms/${id}`
    case 'quoteAccepted':
      return `/my-qms/quote/${id}`
    case 'quoteRejected':
      return `/my-qms/quote/${id}`
    case 'order':
      return `/order/order-overview/${id}`
    case 'agentVerificationStatus':
      return '/dashboard'
    default:
      return '/notification'
  }
}

const NotificationDropdown = props => {
  const [loading, setLoading] = useState(false)
  const [notificationData, setNotificationData] = useState([])
  const [unReadNotificationCount, setUnreadNotificationCount] = useState(0)
  const { user } = useAuth()
  const { t } = useTranslation()

  const fetchData = () => {
    setLoading(true)
    axiosInstance
      .get(ApiEndPoints.NOTIFICATION.list)
      .then(response => {
        const notifications = Array.isArray(response?.data?.data?.notifications)
          ? response.data.data?.notifications
          : []
        setNotificationData(notifications)

        const unreadCount = notifications.filter(n => !n.isRead).length
        setUnreadNotificationCount(unreadCount)
      })
      .catch(error => {
        toastError(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData()
  }, []) // Remove unReadNotificationCount dependency to avoid infinite loop

  // Mark single notification as read
  const markSingleNotificationAsRead = async id => {
    try {
      await axiosInstance.patch(ApiEndPoints.NOTIFICATION.markAsRead(id))

      // Update local state instead of refetching all data
      setNotificationData(prevData =>
        prevData.map(notification =>
          notification.id === id || notification._id === id ? { ...notification, isRead: true } : notification
        )
      )

      // Update unread count
      setUnreadNotificationCount(prevCount => Math.max(0, prevCount - 1))
    } catch (error) {
      toastError(error)
    }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    setLoading(true)
    try {
      // Assuming there's an endpoint to mark all as read, if not, we'll mark each one individually
      const unreadNotifications = notificationData.filter(n => !n.isRead)

      // Mark all unread notifications as read
      const markAsReadPromises = unreadNotifications.map(notification =>
        axiosInstance.patch(ApiEndPoints.NOTIFICATION.markAsRead(notification.id || notification._id))
      )

      await Promise.all(markAsReadPromises)

      // Update local state
      setNotificationData(prevData => prevData.map(notification => ({ ...notification, isRead: true })))
      setUnreadNotificationCount(0)
    } catch (error) {
      toastError(error)
    } finally {
      setLoading(false)
    }
  }

  const { settings } = props
  const [anchorEl, setAnchorEl] = useState(null)
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const { direction } = settings
  const navigate = useNavigate()

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = url => {
    if (url) {
      setTimeout(() => {
        navigate(url)
      }, 1)
    }
    setAnchorEl(null)
  }

  const handleDropdownOnClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClick = async notification => {
    // Only mark as read if it's currently unread
    if (!notification.isRead) {
      await markSingleNotificationAsRead(notification.id || notification._id)
    }

    const redirectUrl = getRedirectUrl(notification)
    handleDropdownClose(redirectUrl)
  }

  const handleReadAllClick = async () => {
    await markAllAsRead()
    handleDropdownClose('/notification')
  }

  const ScrollWrapper = ({ children }) => {
    if (hidden) {
      return <Box sx={{ ...styles, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
    } else {
      return (
        <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
      )
    }
  }

  return (
    <>
      <Fragment>
        <IconButton
          color='inherit'
          aria-haspopup='true'
          onClick={handleDropdownOpen}
          aria-controls='customized-menu'
          sx={{ border: '1px solid #E7E9ED', borderRadius: '40px', bgcolor: '#F9FAFC', height: '48px', width: '48px' }}
        >
          <Badge color='error' overlap='circular' variant='dot' invisible={unReadNotificationCount > 0 ? false : true}>
            <img src={notificationIcon} alt='notificationIcon' />
          </Badge>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleDropdownOnClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        >
          <MenuItem disableRipple>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ fontWeight: 600 }}>{t('Notification.Notifications')}</Typography>
              <CustomChip
                skin='light'
                size='small'
                label={`${unReadNotificationCount} New`}
                color='primary'
                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 500, borderRadius: '10px' }}
              />
            </Box>
          </MenuItem>
          <ScrollWrapper>
            {notificationData?.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant='body2' color='secondary'>
                  {t('Notification.No notifications yet')}
                </Typography>
              </Box>
            ) : (
              notificationData?.map(notification => (
                <MenuItem
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: !notification.isRead ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    '&:hover': {
                      backgroundColor: !notification.isRead ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    {!notification.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          mr: 2,
                          flexShrink: 0
                        }}
                      />
                    )}
                    <Box sx={{ mx: 4, flex: '1 1', display: 'flex', overflow: 'hidden', flexDirection: 'column' }}>
                      <MenuItemTitle>{notification?.title}</MenuItemTitle>
                      <MenuItemSubtitle variant='body2'>{notification?.body}</MenuItemSubtitle>
                    </Box>
                    <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                      {moment(notification.createdAt).format('DD-MM-YYYY')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </ScrollWrapper>
          {notificationData?.length > 0 && (
            <MenuItem
              disableRipple
              sx={{ py: 3.5, borderBottom: 0, borderTop: theme => `1px solid ${theme.palette.divider}` }}
            >
              <Button
                variant='contained'
                onClick={handleReadAllClick}
                sx={{ textDecoration: 'none', width: '100%', textAlign: 'center' }}
              >
                {t('Notification.Read All Notifications')}
              </Button>
            </MenuItem>
          )}
        </Menu>
      </Fragment>
    </>
  )
}

export default NotificationDropdown
